/**
 * Capability detection. The app must keep working when a browser lacks a feature,
 * so every one of these degrades gracefully and never throws.
 */

export const can = {
  wakeLock: typeof navigator !== 'undefined' && 'wakeLock' in navigator,
  fullscreen: typeof document !== 'undefined' && !!document.documentElement.requestFullscreen,
  vibrate: typeof navigator !== 'undefined' && 'vibrate' in navigator,
  battery: typeof navigator !== 'undefined' && 'getBattery' in navigator,
};

let wakeLockRef: WakeLockSentinel | null = null;

export async function requestWakeLock(): Promise<void> {
  if (!can.wakeLock) return;
  try {
    wakeLockRef = await navigator.wakeLock.request('screen');
  } catch {
    /* wake lock unavailable — presenter still works, screen may just dim */
  }
}

export async function releaseWakeLock(): Promise<void> {
  try {
    await wakeLockRef?.release();
  } catch {
    /* ignore */
  }
  wakeLockRef = null;
}

export function vibrate(pattern: number | number[]): void {
  if (can.vibrate) {
    try {
      navigator.vibrate(pattern);
    } catch {
      /* ignore */
    }
  }
}

export async function toggleFullscreen(element: HTMLElement): Promise<void> {
  if (!can.fullscreen) return;
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await element.requestFullscreen();
    }
  } catch {
    /* ignore */
  }
}

/** Returns battery percentage 0..100, or undefined if unavailable. */
export async function readBatteryPct(): Promise<number | undefined> {
  if (!can.battery) return undefined;
  try {
    // getBattery is not in the standard lib types on all targets.
    const nav = navigator as unknown as { getBattery(): Promise<{ level: number }> };
    const battery = await nav.getBattery();
    return Math.round(battery.level * 100);
  } catch {
    return undefined;
  }
}
